import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BiPlus, BiUpload, BiDownload } from "react-icons/bi";
import { HiOutlineEye } from "react-icons/hi";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Header from "../../../components/common/Header/Header";
import CustomButton from "../../../components/common/Button/CustomButton";
import ParticipantImageToolTip from "../../../components/common/Tooltip/ParticipantImageToolTip";
import { Tooltip } from "react-tooltip";
import QuickView from "./ParticipantQuickView";
import SelectOption from "../../../components/common/Select/Select";
import {
  LOADER_STATUS,
  SP_PARTICIPANTS_LIST_LIMIT,
  SORT_TYPE,
  StatusOptions,
  USER_ROLE,
  getAuditParticipationLabel,
  getParticipantImportTemplateFileName,
} from "../../../Constants/constant";
import useCareLabels from "hooks/useCareLabels";
import CareLabelSkeleton from "components/common/CareLabelSkeleton/CareLabelSkeleton";
import TableHeader from "../../../components/common/Table/TableHeader";
import Search from "../../../components/common/Search/Search";
import AvatarGroup from "../../../components/common/Avatar/AvatarGroup";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import Pagination from "components/common/Pagination";
import { formatDateIntoDMYToDisplay, downloadParticipantTemplate, getUrlRole, validateParticipantImportFile } from "Utils/utils";
import { PARTICIPANT_LISTINGS_PER_PAGE_SELECT_OPTIONS } from "Utils/userUiPreferences";
import { roleAccess, usePermissionsVersion } from "helpers/RolesPermission";
import { participantListingSearchPlaceholder } from "Utils/participantListingSearchPlaceholder";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { useCallback } from "react";
import { debounce, has } from "lodash";
import { serviceTypesRequest } from "../../../helpers/apiCalls";
import { trackSearch } from "Utils/trackSearch";

import ModalSkeleton from "../../../components/Modals/ModalSkeleton";
import FileUploadInput from "../../../components/common/FileUpload/FileUploadInput";
import { apiFormDataRequest, apiPostRequest, apiGetRequest } from "../../../helpers/Requests";
import { URL_TEAM_LEAD_GET_MANAGERS_REGIONS_LIST } from "../../../store/services/URL";
import { toast } from "react-toastify";

const ParticipantListTable = (props) => {
  const {
    templateList,
    isLoading,
    queryParams,
    handleFetch,
    renderLoading,
    coSelectedStatus,
    setOpenModal,
    paginationObj,
    setPaginationObj,
    defaultParticipantType,
    settings,
    pageSize = SP_PARTICIPANTS_LIST_LIMIT,
    remoteSyncToken = 0,
    onPageSizeChange,
  } = props;

  const navigate = useNavigate();
  usePermissionsVersion();
  const searchPlaceholder = participantListingSearchPlaceholder({
    ndis: roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.NDIS_NUMBER
    ),
    phone: roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_PHONE
    ),
    email: roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
    ),
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isTemplateDownloading, setIsTemplateDownloading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState(queryParams.sort_field || "first_name");
  const [selectedParticipant, setSelectedParticipant] = useState({});
  const [selectedStatus, setSelectedStatus] = useState({
    label: "Active",
    value: "active",
  });

  const [participantType, setParticipantType] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState({ label: "All", value: "all" });
  const [regions, setRegions] = useState([]);
  const [selectedOptInStatus, setSelectedOptInStatus] = useState({ label: "All", value: "all" });

  const [spDefaultserviceTypes, setSpDefaultServiceTypes] = useState([]);
  
  const userType = localStorage.getItem("userType");
  const {
    careSector,
    participantLabel,
    participantLabelPlural,
    isLoading: isCareLabelsLoading,
  } = useCareLabels();

  const importTemplateFileName = getParticipantImportTemplateFileName(careSector);
  const auditParticipationLabel = getAuditParticipationLabel(careSector);

  useEffect(() => {
    let isMounted = true;

    if (
      Array.isArray(settings?.sp_service_types) &&
      settings.sp_service_types.length > 0 &&
      spDefaultserviceTypes.length === 0
    ) {
      if (!isMounted) return;
      const hasCorePermission = roleAccess(
        [],
        PERMISSION_KEYS.PARTICIPANT,
        PERMISSION_KEYS_CHILDREN.PARTICIPANT.CORE
      );
      const hasCosPermission = roleAccess(
        [],
        PERMISSION_KEYS.PARTICIPANT,
        PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS
      );
      // Regional managers (sp_manager) should have access to core participants
      const isSpManager = userType === "sp_manager";
      serviceTypesRequest()
        .then((res) => {
          const serviceTypes = res?.data
            ?.filter((item) => {
              if (item?.name?.toLowerCase() === "core")
                return hasCorePermission || isSpManager;
              if (item?.name?.toLowerCase() === "cos") return hasCosPermission || isSpManager;
              if (item?.name?.toLowerCase() === "allied health") return true;
              if (item?.name?.toLowerCase() === "aged care") return true;
              return false;
            })
            ?.map((item) => ({
              label: item.name,
              value: item.id,
            }));
          setSpDefaultServiceTypes(serviceTypes);

          if (userType === "sp_manager") {
            setParticipantType(serviceTypes);
            if (serviceTypes?.length > 0) {
              debouncedHandleFetch(
                {
                  participant_type: serviceTypes?.map((type) => type.value),
                  status: selectedStatus?.value,
                  sc_id: coSelectedStatus?.id,
                  region_id: selectedRegion?.value !== "all" ? selectedRegion?.value : undefined,
                  opt_in_status: selectedOptInStatus?.value !== "all" ? selectedOptInStatus?.value : undefined,
                  page: 1,
                },
                sortBy
              );
            }
          } else {
            const defaultType = serviceTypes.filter((type) =>
              settings.sp_service_types.includes(type.value)
            );
            setParticipantType(defaultType);
            if (defaultType.length > 0) {
              debouncedHandleFetch(
                {
                  participant_type: defaultType.map((type) => type.value),
                  status: selectedStatus?.value,
                  sc_id: coSelectedStatus?.id,
                  region_id: selectedRegion?.value !== "all" ? selectedRegion?.value : undefined,
                  opt_in_status: selectedOptInStatus?.value !== "all" ? selectedOptInStatus?.value : undefined,
                  page: 1,
                },
                sortBy
              );
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching service types:", err);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [settings?.sp_service_types]);

  // Server preference differed from cache — refresh once with the corrected page size.
  useEffect(() => {
    if (remoteSyncToken === 0) return;
    if (!Array.isArray(participantType) || participantType.length === 0) return;
    handleFetch(
      {
        participant_type: participantType.map((type) => type.value),
        status: selectedStatus?.value,
        sc_id: coSelectedStatus?.id,
        region_id:
          selectedRegion?.value !== "all" ? selectedRegion?.value : undefined,
        opt_in_status:
          selectedOptInStatus?.value !== "all"
            ? selectedOptInStatus?.value
            : undefined,
        page: 1,
      },
      sortBy
    );
  }, [remoteSyncToken]);

  const [sortDescending, setSortDescending] = useState(
    Boolean(queryParams.sort_field) && queryParams.sort_order === SORT_TYPE.DESC
  );

  const debouncedHandleFetch = useCallback(
    debounce((params, sortBy) => {
      handleFetch(params, sortBy);
    }, 800),
    [handleFetch]
  );

  const handleServiceSupportChange = (e) => {
    setParticipantType(e);
    debouncedHandleFetch(
      {
        participant_type: e?.map((type) => type.value),
        status: selectedStatus?.value,
        sc_id: coSelectedStatus?.id,
        region_id: selectedRegion?.value !== "all" ? selectedRegion?.value : undefined,
        opt_in_status: selectedOptInStatus?.value !== "all" ? selectedOptInStatus?.value : undefined,
        page: 1,
      },
      sortBy
    );
  };

  const handleRegionChange = (e) => {
    setSelectedRegion(e);
    handleFetch(
      {
        participant_type: participantType?.map((type) => type.value),
        status: selectedStatus?.value,
        sc_id: coSelectedStatus?.id,
        region_id: e?.value !== "all" ? e?.value : undefined,
        opt_in_status: selectedOptInStatus?.value !== "all" ? selectedOptInStatus?.value : undefined,
        page: 1,
      },
      sortBy
    );
  };

  const handleOptInStatusChange = (e) => {
    // Always update state and fetch with new filter, even during loading.
    // The participants-list saga uses takeLatest, so the new request will cancel
    // the in-flight one and the filtered response will be the one that updates the list.
    setSelectedOptInStatus(e);
    handleFetch(
      {
        participant_type: participantType?.map((type) => type.value),
        status: selectedStatus?.value,
        sc_id: coSelectedStatus?.id,
        region_id: selectedRegion?.value !== "all" ? selectedRegion?.value : undefined,
        opt_in_status: e?.value !== "all" ? e?.value : undefined,
        page: 1,
      },
      sortBy
    );
  };

  const optInStatusOptions = [
    { label: "All", value: "all" },
    { label: "Opt-in for Audit", value: "opted_in" },
    { label: "Opt-out for Audit", value: "opted_out" },
  ];

  // Fetch regions list
  useEffect(() => {
    const fetchRegionsList = async () => {
      try {
        const res = await apiGetRequest(URL_TEAM_LEAD_GET_MANAGERS_REGIONS_LIST);
        if (res?.data?.data) {
          const regionsList = [
            { label: "All", value: "all" },
            ...res.data.data.map((el) => ({
              label: el.name,
              value: el.id,
            })),
          ];
          setRegions(regionsList);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
      }
    };

    if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
      fetchRegionsList();
    }
  }, []);

  useEffect(() => {
    if (search === null) return;
    const timeOutId = setTimeout(() => {
      trackSearch("participant", search);
      handleFetch(
        {
          search: search,
          sc_id: coSelectedStatus?.id,
          participant_type: participantType?.map((type) => type.value),
          status: selectedStatus?.value,
        },
        sortBy
      );
    }, 1000);
    return () => clearTimeout(timeOutId);
  }, [search, coSelectedStatus, sortBy]);

 

  const applySort = (sortField, sortOrder) => {
    setSortBy(sortField);
    setSortDescending(sortOrder === SORT_TYPE.DESC);
    handleFetch(
      {
        sort_field: sortField,
        sort_order: sortOrder,
        page: 1,
        participant_type: participantType?.map((type) => type.value),
        status: selectedStatus?.value,
      },
      sortField
    );
  };

  const flipSort = (by) => {
    const isAz = sortBy === "first_name" && !sortDescending;
    const isZa = sortBy === "first_name" && sortDescending;
    if (by === "created_at" || isZa) {
      applySort("created_at", SORT_TYPE.DESC);
      return;
    }
    if (isAz) {
      applySort("first_name", SORT_TYPE.DESC);
      return;
    }
    applySort("first_name", SORT_TYPE.ASC);
  };

 

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  return (
    <>
      <Header
        title={
          isCareLabelsLoading ? (
            <CareLabelSkeleton width="w-32" height="h-6" />
          ) : (
            participantLabelPlural
          )
        }
        btnComponent={
          <div className='flex flex-wrap items-center justify-end gap-2 sm:gap-2 md:gap-4'>
            <CustomButton
              clickHandler={handleImportClick}
              label={
                isCareLabelsLoading ? (
                  <CareLabelSkeleton width="w-36" />
                ) : (
                  <span className='whitespace-nowrap'>
                    <span className='hidden lg:inline'>Import {participantLabelPlural} list</span>
                    <span className='lg:hidden'>Import</span>
                  </span>
                )
              }
              Icon={<BiUpload />}
              variant='primary'
              cssClass='text-xs sm:text-sm px-2 sm:px-3 md:px-4'
              isDisabled={isCareLabelsLoading}
            />
            <CustomButton
              clickHandler={() => setOpenModal(true)}
              label={
                isCareLabelsLoading ? (
                  <CareLabelSkeleton width="w-32" />
                ) : (
                  <span className='whitespace-nowrap'>
                    <span className='hidden lg:inline'>Add New {participantLabel}</span>
                    <span className='lg:hidden'>Add New</span>
                  </span>
                )
              }
              Icon={<BiPlus />}
              variant='primary'
              cssClass='text-xs sm:text-sm px-2 sm:px-3 md:px-4'
              isDisabled={isCareLabelsLoading}
            />
          </div>
        }
      />

      <div className='w-full px-3 sm:px-4 md:px-6 lg:px-8'>
        {/* Modern Filters Section */}
        <div
          style={{ paddingTop: "15px", top: "70px" }}
          className='sticky py-4 sm:py-5 bg-white z-[6] border-b border-gray-200 shadow-sm rounded-t-xl'
        >
          <div className='flex flex-col gap-4'>
            {/* Top Row: Filters */}
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4'>
              <div className='w-full'>
                <label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2'>
                  Status
                </label>
                <div className='relative'>
                  <SelectOption
                    isDisabled={isLoading}
                    isSearch={false}
                    selectedOption={selectedStatus}
                    handleChange={(e) => {
                      setSelectedStatus(e);
                      handleFetch(
                        {
                          status: e.value,
                          participant_type: participantType?.map(
                            (type) => type.value
                          ),
                          sc_id: coSelectedStatus?.id,
                          page: 1,
                        },
                        sortBy
                      );
                    }}
                    data={StatusOptions}
                    cssClass='z-20'
                  />
                </div>
              </div>
              {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                <div className='w-full'>
                  <label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2'>
                    Service Support
                  </label>
                  <SelectOption
                    isDisabled={isLoading}
                    isMulti={true}
                    isSearch={false}
                    selectedOption={participantType}
                    handleChange={handleServiceSupportChange}
                    data={spDefaultserviceTypes}
                    cssClass='z-20'
                  />
                </div>
              )}
              {/* Audit Participation Filter */}
              {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                <div className='w-full'>
                  <label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5'>
                    <span>{auditParticipationLabel}</span>
                    <a data-tooltip-id='audit-participation-tooltip'>
                      <AiOutlineInfoCircle className='w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help' />
                    </a>
                    <Tooltip
                      id='audit-participation-tooltip'
                      place='top'
                      content={
                        isCareLabelsLoading
                          ? "Filter based on audit participation settings."
                          : `Filter ${participantLabelPlural.toLowerCase()} based on whether they are included in audit reports and compliance checks. This setting can be configured in each ${participantLabel.toLowerCase()}'s settings page.`
                      }
                      variant='dark'
                      className='z-[501] max-w-xs'
                    />
                  </label>
                  <SelectOption
                    isDisabled={isLoading}
                    isSearch={false}
                    selectedOption={selectedOptInStatus}
                    handleChange={handleOptInStatusChange}
                    data={optInStatusOptions}
                    cssClass='z-20'
                  />
                </div>
              )}
              {/* Search */}
              <div className='w-full'>
                <label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2'>
                  Search{" "}
                  {isCareLabelsLoading ? (
                    <CareLabelSkeleton width="w-24" height="h-3.5" className="align-middle" />
                  ) : (
                    participantLabelPlural
                  )}
                </label>
                <div className='relative'>
                  <Search 
                    setSearch={setSearch} 
                    search={search}
                    placeholder={searchPlaceholder}
                    width='w-full'
                    customClassSearch='border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg shadow-sm transition-all w-full'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='py-4 sm:py-6'>
          <div className='flex flex-col'>
            <div className='min-w-full overflow-hidden'>
              <div className='bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden'>
                <div className='table-wrp block'>
                  {isLoading ? (
                    <div
                      className={`flex flex-col w-full min-h-[400px] bg-gradient-to-br from-gray-50 to-blue-50 items-center justify-center rounded-xl`}
                    >
                      <CircularLoader
                        classes='flex justify-center items-center'
                        classloader='loader-l'
                      />
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className='hidden lg:block overflow-x-auto custom-scroll'>
                        <table className='min-w-full divide-y divide-gray-200'>
                        <thead
                          style={{ top: "0px" }}
                          className='bg-gradient-to-r from-gray-50 via-gray-50 to-gray-100 sticky z-[5] border-b-2 border-gray-200'
                        >
                        <tr>
                          <TableHeader
                            sortName='first_name'
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label={
                              isCareLabelsLoading ? (
                                <CareLabelSkeleton width="w-28" height="h-3.5" />
                              ) : (
                                `${participantLabel} Name`
                              )
                            }
                            cssClass='bg-blue-50 pl-4'
                          />
                          {roleAccess(
                            [],
                            PERMISSION_KEYS.PARTICIPANT,
                            PERMISSION_KEYS_CHILDREN.PARTICIPANT.NDIS_NUMBER
                          ) && (
                            <TableHeader
                              cssClass='sm:pl-3 bg-blue-50 px-0'
                              isSort={false}
                              sortName='ndis_no'
                              flipSort={flipSort}
                              sortBy={sortBy}
                              sortDescending={sortDescending}
                              label='NDIS No.'
                            />
                          )}
                          {(roleAccess(
                            [],
                            PERMISSION_KEYS.PARTICIPANT,
                            PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                          ) ||
                            roleAccess(
                              [],
                              PERMISSION_KEYS.PARTICIPANT,
                              PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_PHONE
                            )) && (
                            <TableHeader
                              cssClass='sm:pl-3 bg-blue-50 px-0'
                              isSort={false}
                              sortName='contact_information'
                              flipSort={flipSort}
                              sortBy={sortBy}
                              sortDescending={sortDescending}
                              label={"Contact"}
                            
                            />
                          )}
                          <TableHeader
                            cssClass='sm:pl-3 bg-blue-50 px-0'
                            isSort={false}
                            sortName='status'
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label={"Status"}
                           
                          />
                          {roleAccess([
                            USER_ROLE.Sp_Admin,
                            USER_ROLE.Sp_manager,
                            PERMISSION_KEYS.PARTICIPANT,
                            PERMISSION_KEYS_CHILDREN.PARTICIPANT.CORE,
                            PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS,
                          ]) && (
                            <TableHeader
                              cssClass='sm:pl-3 bg-blue-50 px-0'
                              isSort={false}
                              sortName='participant_type'
                              flipSort={flipSort}
                              sortBy={sortBy}
                              sortDescending={sortDescending}
                              label={"Service Type"}
                              
                            />
                          )}
                          <TableHeader
                            cssClass='sm:pl-3 w-[20%] bg-blue-50 px-0'
                            isSort={false}
                            sortName='workers'
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label={"Assigned Workers"}
                            
                          />

                          <TableHeader
                            isSort={false}
                            sortName='created_at'
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label={"Created Date"}
                            cssClass='bg-blue-50 px-0'
                          />

                          <th
                            scope='col'
                            className='bg-blue-50 text-gray-900 py-4 pl-3 pr-4 text-left text-sm font-semibold'
                          >
                            Actions
                            <span className='bg-blue-50 sr-only'>View</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-100 bg-white'>
                        {templateList?.length > 0 &&
                          templateList?.map((participant, index) => {
                            const hasDetailPermission = roleAccess(
                              [],
                              PERMISSION_KEYS.PARTICIPANT,
                              PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
                            );
                            const profileUrl = `/sp-profile/${participant.uuid}`;
                            
                            return (
                            <tr
                              key={index}
                              className='bg-white hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-cyan-50/30 transition-all duration-200 border-b border-gray-100 group even:bg-gray-50/30'
                            >
                              <td className='whitespace-nowrap py-4 px-4 text-sm'>
                                <div className='flex items-center'>
                                  <div className='h-11 w-11 flex-shrink-0 relative'>
                                    
                                    <ParticipantImageToolTip
                                      cssClass={
                                        "py-2 px-3 rounded-lg text-white z-[9] shadow-lg"
                                      }
                                      participant={participant}
                                      imgCssClass={
                                        "capitalize object-cover h-11 w-11 rounded-full ring-2 ring-gray-200 group-hover:ring-blue-400 transition-all"
                                      }
                                    />
                                  </div>
                                  <div className='ml-4 capitalize min-w-0 flex-1'>
                                    {hasDetailPermission ? (
                                      <Link
                                        to={profileUrl}
                                        onClick={(e) => {
                                          sessionStorage.clear();
                                        }}
                                        title={`${participant.first_name} ${participant.last_name}`}
                                        className='block'
                                      >
                                        <p className='font-semibold text-gray-900 truncate max-w-[200px] cursor-pointer hover:text-blue-600 transition-colors'>
                                          {`${participant.first_name} ${participant.last_name}`}
                                        </p>
                                      </Link>
                                    ) : (
                                      <span title={`${participant.first_name} ${participant.last_name}`}>
                                        <p className='font-semibold text-gray-900 truncate max-w-[200px]'>
                                          {`${participant.first_name} ${participant.last_name}`}
                                        </p>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              {roleAccess(
                                [],
                                PERMISSION_KEYS.PARTICIPANT,
                                PERMISSION_KEYS_CHILDREN.PARTICIPANT.NDIS_NUMBER
                              ) && (
                                <td className='whitespace-nowrap px-4 py-4 text-sm text-gray-900'>
                                  {participant.ndis_no || "—"}
                                </td>
                              )}
                              {(roleAccess(
                                [],
                                PERMISSION_KEYS.PARTICIPANT,
                                PERMISSION_KEYS_CHILDREN.PARTICIPANT
                                  .PRIMARY_EMAIL
                              ) ||
                                roleAccess(
                                  [],
                                  PERMISSION_KEYS.PARTICIPANT,
                                  PERMISSION_KEYS_CHILDREN.PARTICIPANT
                                    .PRIMARY_PHONE
                                )) && (
                                <td className='whitespace-nowrap px-4 py-4 text-sm'>
                                  {roleAccess(
                                    [],
                                    PERMISSION_KEYS.PARTICIPANT,
                                    PERMISSION_KEYS_CHILDREN.PARTICIPANT
                                      .PRIMARY_PHONE
                                  ) && (
                                    <div className='text-gray-900 font-medium'>
                                      {participant.primary_no}
                                    </div>
                                  )}
                                  {roleAccess(
                                    [],
                                    PERMISSION_KEYS.PARTICIPANT,
                                    PERMISSION_KEYS_CHILDREN.PARTICIPANT
                                      .PRIMARY_EMAIL
                                  ) && (
                                    <div className='text-gray-600 text-xs mt-1 truncate max-w-[200px]'>
                                      {participant.email}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td className='whitespace-nowrap px-4 py-4 text-sm'>
                                <span
                                  className={`${
                                    participant.status === "active"
                                      ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                                      : "bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200"
                                  } inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold leading-5 capitalize shadow-sm`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    participant.status === "active" ? "bg-green-500" : "bg-red-500"
                                  }`}></span>
                                  {participant.status}
                                </span>
                              </td>
                              {/* <td className='whitespace-nowrap px-2 py-4 text-sm text-gray-500 capitalize'>
                                {participant.participant_type || "N/A"}
                              </td> */}
                              {roleAccess([
                                USER_ROLE.Sp_Admin,
                                USER_ROLE.Sp_manager,
                                PERMISSION_KEYS.PARTICIPANT,
                                PERMISSION_KEYS_CHILDREN.PARTICIPANT.CORE,
                                PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS,
                              ]) && (
                              <td className='whitespace-nowrap px-4 py-5 text-sm'>
                                <div className='flex flex-wrap gap-1.5'>
                                    {participant.participant_type && participant.participant_type.length > 0 ? (
                                      participant.participant_type.map((type, idx) => (
                                        <span
                                          key={idx}
                                          className='inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-200 shadow-sm'
                                        >
                                          {type.name}
                                        </span>
                                      ))
                                    ) : (
                                      <span className='text-gray-400 text-xs italic'>N/A</span>
                                    )}
                                  </div>
                                </td>
                              )}
                              <td className='px-4 py-5 text-sm'>
                                <div className='flex items-center'>
                                  {participant.workers && participant.workers.length > 0 ? (
                                    <AvatarGroup
                                      type='worker'
                                      arrayData={participant.workers}
                                      cssClass='mt-0'
                                      maxLength={10}
                                    />
                                  ) : (
                                    <span className='text-gray-400 text-xs italic'>No workers assigned</span>
                                  )}
                                </div>
                              </td>
                              <td className='whitespace-nowrap py-5 px-4 text-sm text-gray-600 font-semibold'>
                                {formatDateIntoDMYToDisplay(
                                  participant.created_at
                                )}
                              </td>
                              <td className='relative whitespace-nowrap py-5 pl-4 pr-4 text-center text-sm font-medium'>
                                <div className='flex items-center justify-center gap-x-2'>
                                  {/* <Link
                                    to={`/sp-profile/${participant.uuid}`}
                                    className="icon-pencil-wrapper"
                                    title="Edit"
                                  >
                                    <BiPencil className="icon-size" />
                                    <span className="sr-only">
                                      {participant.uuid}
                                    </span>
                                  </Link> */}

                                  <div
                                    onClick={() => {
                                      setIsOpen(true);
                                      setSelectedParticipant(participant);
                                    }}
                                    tabIndex={0}
                                    role='button'
                                    className='p-2.5 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 text-blue-600 hover:text-blue-700 transition-all duration-200 cursor-pointer border border-blue-200 hover:border-blue-400 hover:shadow-md group'
                                    title='View Details'
                                  >
                                    <HiOutlineEye className='w-5 h-5 group-hover:scale-110 transition-transform' />
                                    <span className='sr-only'>
                                      {participant.uuid}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        {renderLoading === LOADER_STATUS.COMPLATE &&
                          templateList?.length === 0 && (
                            <tr>
                              <td
                                colSpan={8}
                                className='py-16 text-center'
                              >
                                <div className='flex flex-col items-center justify-center'>
                                  <div className='w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4'>
                                    <svg className='w-8 h-8 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' />
                                    </svg>
                                  </div>
                                  <p className='text-gray-500 font-medium text-base'>
                                    No{" "}
                                    {isCareLabelsLoading ? (
                                      <CareLabelSkeleton width="w-20" height="h-3.5" className="bg-gray-300 align-middle" />
                                    ) : (
                                      `${participantLabelPlural.toLowerCase()} found`
                                    )}
                                  </p>
                                  <p className='text-gray-400 text-sm mt-1'>Try adjusting your filters or search criteria</p>
                                </div>
                              </td>
                            </tr>
                          )}
                      </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className='lg:hidden p-4 space-y-4'>
                        {templateList?.length > 0 ? (
                          templateList.map((participant, index) => (
                            <div
                              key={index}
                              className='bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200'
                            >
                              {/* Header with Avatar and Name */}
                              <div className='flex items-start justify-between mb-3'>
                                <div className='flex items-center gap-3 flex-1 min-w-0'>
                                  <div className='h-12 w-12 flex-shrink-0'>
                                    <ParticipantImageToolTip
                                      cssClass="bg-gray-900 py-2 px-3 rounded-lg text-white z-[9] shadow-lg"
                                      participant={participant}
                                      imgCssClass="capitalize object-cover h-12 w-12 rounded-full ring-2 ring-gray-200"
                                    />
                                  </div>
                                  <div className='flex-1 min-w-0'>
                                    {roleAccess(
                                      [],
                                      PERMISSION_KEYS.PARTICIPANT,
                                      PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
                                    ) ? (
                                      <Link
                                        to={`/sp-profile/${participant.uuid}`}
                                        onClick={(e) => {
                                          sessionStorage.clear();
                                        }}
                                        className='block'
                                      >
                                        <h3 className='font-bold text-gray-900 capitalize truncate cursor-pointer hover:text-blue-600 transition-colors'>
                                          {participant.first_name} {participant.last_name}
                                        </h3>
                                      </Link>
                                    ) : (
                                      <h3 className='font-bold text-gray-900 capitalize truncate'>
                                        {participant.first_name} {participant.last_name}
                                      </h3>
                                    )}
                                    {roleAccess(
                                      [],
                                      PERMISSION_KEYS.PARTICIPANT,
                                      PERMISSION_KEYS_CHILDREN.PARTICIPANT.NDIS_NUMBER
                                    ) && (
                                      <p className='text-xs text-gray-500 mt-0.5 truncate'>
                                        {participant.ndis_no || "—"}
                                      </p>
                                    )}
                                    <div className='mt-1'>
                                      <span
                                        className={`${
                                          participant.status === "active"
                                            ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                                            : "bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200"
                                        } inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold capitalize shadow-sm`}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          participant.status === "active" ? "bg-green-500" : "bg-red-500"
                                        }`}></span>
                                        {participant.status}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  onClick={() => {
                                    setIsOpen(true);
                                    setSelectedParticipant(participant);
                                  }}
                                  className='p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-all cursor-pointer border border-blue-200'
                                >
                                  <HiOutlineEye className='w-5 h-5' />
                                </div>
                              </div>

                              {/* Contact Information */}
                              {(roleAccess(
                                [],
                                PERMISSION_KEYS.PARTICIPANT,
                                PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                              ) ||
                                roleAccess(
                                  [],
                                  PERMISSION_KEYS.PARTICIPANT,
                                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_PHONE
                                )) && (
                                <div className='mb-3 space-y-1.5'>
                                  {roleAccess(
                                    [],
                                    PERMISSION_KEYS.PARTICIPANT,
                                    PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_PHONE
                                  ) && (
                                    <div className='text-sm text-gray-900 font-medium'>
                                      {participant.primary_no}
                                    </div>
                                  )}
                                  {roleAccess(
                                    [],
                                    PERMISSION_KEYS.PARTICIPANT,
                                    PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                                  ) && (
                                    <div className='text-sm text-gray-600 truncate'>
                                      {participant.email}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Service Support & Workers Row */}
                              <div className='grid grid-cols-2 gap-3 mb-3'>
                                {roleAccess([
                                  USER_ROLE.Sp_Admin,
                                  USER_ROLE.Sp_manager,
                                  PERMISSION_KEYS.PARTICIPANT,
                                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.CORE,
                                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS,
                                ]) && (
                                  <div>
                                    <p className='text-xs font-semibold text-gray-500 mb-1.5'>Service Support</p>
                                    <div className='flex flex-wrap gap-1.5'>
                                      {participant.participant_type && participant.participant_type.length > 0 ? (
                                        participant.participant_type.map((type, idx) => (
                                          <span
                                            key={idx}
                                            className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-200'
                                          >
                                            {type.name}
                                          </span>
                                        ))
                                      ) : (
                                        <span className='text-gray-400 text-xs italic'>N/A</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <p className='text-xs font-semibold text-gray-500 mb-1.5'>Workers</p>
                                  {participant.workers && participant.workers.length > 0 ? (
                                    <AvatarGroup
                                      type='worker'
                                      arrayData={participant.workers}
                                      cssClass='mt-0'
                                      maxLength={5}
                                    />
                                  ) : (
                                    <span className='text-gray-400 text-xs italic'>No workers assigned</span>
                                  )}
                                </div>
                              </div>

                              {/* Created Date */}
                              <div className='pt-3 border-t border-gray-100'>
                                <div className='flex items-center justify-between'>
                                  <div>
                                    <p className='text-xs font-semibold text-gray-500 mb-0.5'>Created</p>
                                    <p className='text-sm text-gray-700 font-medium'>
                                      {formatDateIntoDMYToDisplay(participant.created_at)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : renderLoading === LOADER_STATUS.COMPLATE ? (
                          <div className='bg-white border border-gray-200 rounded-xl p-12 text-center'>
                            <div className='flex flex-col items-center justify-center'>
                              <div className='w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4'>
                                <svg className='w-8 h-8 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' />
                                </svg>
                              </div>
                              <p className='text-gray-500 font-medium text-base'>
                                No{" "}
                                {isCareLabelsLoading ? (
                                  <CareLabelSkeleton width="w-20" height="h-3.5" className="bg-gray-300 align-middle" />
                                ) : (
                                  `${participantLabelPlural.toLowerCase()} found`
                                )}
                              </p>
                              <p className='text-gray-400 text-sm mt-1'>Try adjusting your filters or search criteria</p>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* side over Modal */}
        {isOpen && (
          <QuickView
            paddingLeft='px-0'
            selectedParticipant={selectedParticipant}
            isOpen={isOpen}
            setIsOpen={() => {
              setIsOpen(false);
              setSelectedParticipant({});
            }}
          />
        )}
      </div>
      {/* Import Participant Modal */}
      {isImportModalOpen && (
        <ModalSkeleton
          isOpen={isImportModalOpen}
          closeModal={() => {
            setIsImportModalOpen(false);
            setImportFile(null);
            setValidationErrors([]);
            setSuccessMessage(null);
          }}
          title={
            isCareLabelsLoading ? (
              <CareLabelSkeleton width="w-40" height="h-5" />
            ) : (
              `Import ${participantLabelPlural} list`
            )
          }
          cssClass='w-[90%] sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[55%]'
        >
          <div className='flex flex-col max-h-[70vh]'>
            {/* Scrollable Content */}
            <div className='flex-1 overflow-y-auto p-6 space-y-6'>
            {/* Download Template Section */}
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='bg-blue-100 p-2 rounded-lg'>
                    <BiDownload className='text-blue-600 text-xl' />
                  </div>
                  <div>
                    <h3 className='text-sm font-semibold text-gray-900'>
                      Download Template
                    </h3>
                    <p className='text-xs text-gray-600 mt-0.5'>
                      {isCareLabelsLoading ? (
                        <CareLabelSkeleton width="w-48" height="h-3" />
                      ) : (
                        <>
                          Download <span className='font-medium'>{importTemplateFileName}</span> to
                          ensure correct format
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setIsTemplateDownloading(true);
                    try {
                      await downloadParticipantTemplate(careSector);
                    } catch (error) {
                      console.error("Error downloading template:", error);
                    } finally {
                      setIsTemplateDownloading(false);
                    }
                  }}
                  disabled={isTemplateDownloading}
                  className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isTemplateDownloading ? (
                    <>
                      <svg
                        role="status"
                        className="animate-spin h-4 w-4 text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <BiDownload className='text-base' />
                      <span>Download</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* File Upload Section */}
            <div>
              <label className='block text-sm font-semibold text-gray-900 mb-2'>
                Browse / Upload File
              </label>
              <FileUploadInput
                handleFile={(file) => {
                  setImportFile(file);
                  setValidationErrors([]); // Clear errors when new file is selected
                  setSuccessMessage(null); // Clear success message when new file is selected
                }}
                handleDelete={() => {
                  setImportFile(null);
                  setValidationErrors([]); // Clear errors when file is deleted
                  setSuccessMessage(null); // Clear success message when file is deleted
                }}
                file={importFile}
                type='xlsx'
                size={20}
                showDragAndDrop={true}
              />
              <p className='mt-2 text-xs text-gray-500 flex items-center gap-1'>
                <AiOutlineInfoCircle className='text-gray-400' />
                Supported formats: <span className='font-semibold'>.xlsx</span>
              </p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className='bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg'>
                <div className='flex items-start gap-2'>
                  <AiOutlineInfoCircle className='text-green-600 text-lg mt-0.5 flex-shrink-0' />
                  <div className='flex-1'>
                    <h4 className='text-sm font-semibold text-green-800 mb-2'>
                      Success:
                    </h4>
                    <div className='text-sm text-green-700 whitespace-pre-line'>
                      {successMessage}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Errors / Warnings */}
            {validationErrors.length > 0 && (
              <div className='bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg'>
                <div className='flex items-start gap-2'>
                  <AiOutlineInfoCircle className='text-red-600 text-lg mt-0.5 flex-shrink-0' />
                  <div className='flex-1'>
                    <h4 className='text-sm font-semibold text-red-800 mb-2'>
                      Errors Found:
                    </h4>
                    <ul className='text-sm text-red-700 space-y-1 max-h-48 overflow-y-auto'>
                      {validationErrors.map((error, index) => (
                        <li key={index} className='flex items-start gap-2'>
                          <span className='text-red-600 mt-1'>•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                    <p className='text-xs text-red-600 mt-2 font-medium'>
                      Please fix these errors before importing.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Important Note */}
            <div className='bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg'>
              <div className='flex items-start gap-2'>
                <AiOutlineInfoCircle className='text-amber-600 text-lg mt-0.5 flex-shrink-0' />
                <div className='text-sm text-amber-800 space-y-1'>
                  <p>
                    <strong>IMPORTANT:</strong> Do not change the column headings provided in the template. These must remain unchanged for the import to work correctly.
                  </p>
                  <p>
                    <strong>Before proceeding with your data, please remove the dummy data row.</strong> The template includes a dummy row for reference - delete it and enter your actual {participantLabel.toLowerCase()} data.
                  </p>
                  <p>
                    Fields marked with an asterisk (*) are required fields and must be filled in.
                  </p>
                </div>
              </div>
            </div>
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className='flex-shrink-0 border-t border-gray-200 bg-white p-6'>
              <div className='flex justify-end gap-3'>
              <CustomButton
                label='Cancel'
                clickHandler={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setValidationErrors([]);
                  setSuccessMessage(null);
                }}
                variant='secondary'
              />
              <CustomButton
                label='Import'
                clickHandler={async () => {
                  if (!importFile) {
                    setValidationErrors(["Please select a file to import"]);
                    return;
                  }
                  
                  // Clear previous errors before importing
                  setValidationErrors([]);
                  setSuccessMessage(null);
                  
                  setIsImporting(true);
                  try {
                    const formData = new FormData();
                    formData.append("file", importFile);
                    
                    const rolePrefix = getUrlRole();
                    const response = await apiFormDataRequest(`${rolePrefix}/import-data/participant`, formData);
                    
                    console.log("Import response:", response);
                    
                    // Check for success - axios response structure
                    const isSuccess = response && (
                      response.status === 200 || 
                      response.status === 201 || 
                      (response.data && !response.data.error) ||
                      (!response.error && response.data)
                    );
                    
                    if (isSuccess) {
                      const importData = response?.data?.data || response?.data || {};
                      const imported = importData.imported || 0;
                      const importedNames = importData.imported_names || [];
                      const skipped = importData.skipped || 0;
                      const skippedNames = importData.skipped_names || [];
                      const skippedDetails = importData.skipped_details || [];
                      
                      // Always show success message if any records were imported (even if there are errors)
                      if (imported > 0) {
                        let successMessage = `${imported} ${participantLabel.toLowerCase()}(s) imported successfully.`;
                        if (importedNames.length > 0) {
                          const nameList = importedNames.slice(0, 10).join(', ');
                          const moreText = importedNames.length > 10 ? ` and ${importedNames.length - 10} more` : '';
                          successMessage += `\n\nImported ${participantLabel}: ${nameList}${moreText}.`;
                        }
                        setSuccessMessage(successMessage);
                      } else {
                        setSuccessMessage(null);
                      }
                      
                      // Show error messages for skipped records
                      const errorMessages = [];
                      if (skipped > 0) {
                        // Prioritize skippedDetails to show actual error reasons (NDIS format errors, etc.)
                        if (skippedDetails.length > 0) {
                          // Use detailed skipped information to show actual error reasons
                          skippedDetails.slice(0, 20).forEach(detail => {
                            if (detail.reason) {
                              // Show the actual error reason from backend (already includes row numbers)
                              errorMessages.push(detail.reason);
                            }
                          });
                          if (skippedDetails.length > 20) {
                            errorMessages.push(`and ${skippedDetails.length - 20} more record(s) skipped.`);
                          }
                        } else if (skippedNames.length > 0) {
                          // Fallback to generic message if no detailed reasons available
                          const nameList = skippedNames.slice(0, 5).join(', ');
                          const moreText = skippedNames.length > 5 ? ` and ${skippedNames.length - 5} more` : '';
                          errorMessages.push(`${skipped} record(s) skipped (duplicate names: ${nameList}${moreText}).`);
                        } else {
                          errorMessages.push(`${skipped} record(s) skipped due to duplicates or errors.`);
                        }
                      }
                      
                      setValidationErrors(errorMessages);
                      setIsImporting(false);
                      
                      // Only close modal if there are no errors (all records imported successfully)
                      if (imported > 0 && skipped === 0) {
                        // Close modal and refresh after a short delay to show success message
                        setTimeout(() => {
                          setIsImportModalOpen(false);
                          setImportFile(null);
                          setValidationErrors([]);
                          setSuccessMessage(null);
                      
                          // Refresh the list with current filters
                          handleFetch(
                            {
                              participant_type: participantType?.map((type) => type.value),
                              status: selectedStatus?.value,
                              sc_id: coSelectedStatus?.id,
                              page: 1,
                            },
                            sortBy
                          );
                        }, 2000);
                      }
                      // If there are errors, keep modal open so user can see both success and error messages
                    } else {
                      const errorMessage = response?.data?.message || response?.message || `Failed to import ${participantLabelPlural.toLowerCase()}. Please check the file format and try again.`;
                      setValidationErrors([errorMessage]);
                      setIsImporting(false);
                    }
                  } catch (error) {
                    console.error("Import error:", error);
                    const errorMessage = error?.response?.data?.message || error?.message || `Failed to import ${participantLabelPlural.toLowerCase()}. Please check the file format and try again.`;
                    setValidationErrors([errorMessage]);
                    setIsImporting(false);
                  }
                }}
                variant='primary'
                isDisabled={!importFile || isImporting}
                showLoading={isImporting}
              />
              </div>
            </div>
          </div>
        </ModalSkeleton>
      )}
      {renderLoading === LOADER_STATUS.COMPLATE &&
        !isLoading &&
        templateList?.length > 0 &&
        paginationObj.total > 0 && (
          <div className='bg-white w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 border-t border-gray-200'>
            <Pagination
              page={paginationObj.page}
              activeListLength={templateList}
              limit={pageSize || SP_PARTICIPANTS_LIST_LIMIT}
              totalList={paginationObj.total}
              showPerPageDropdown={Boolean(onPageSizeChange)}
              perPageData={PARTICIPANT_LISTINGS_PER_PAGE_SELECT_OPTIONS}
              perPageValue={{
                label: String(pageSize || SP_PARTICIPANTS_LIST_LIMIT),
                value: pageSize || SP_PARTICIPANTS_LIST_LIMIT,
              }}
              handleLimitChange={(option) => {
                if (!onPageSizeChange) return;
                onPageSizeChange(option);
                const obj = { ...paginationObj, page: 1 };
                setPaginationObj(obj);
                handleFetch(
                  {
                    status: selectedStatus?.value,
                    participant_type: participantType?.map(
                      (type) => type.value
                    ),
                    page: 1,
                    region_id:
                      selectedRegion?.value !== "all"
                        ? selectedRegion?.value
                        : undefined,
                    opt_in_status:
                      selectedOptInStatus?.value !== "all"
                        ? selectedOptInStatus?.value
                        : undefined,
                  },
                  sortBy
                );
              }}
              handlePagination={(e) => {
                const obj = { ...paginationObj, page: e };
                setPaginationObj(obj);
                handleFetch(
                  {
                    status: selectedStatus?.value,
                    participant_type: participantType?.map(
                      (type) => type.value
                    ),
                    page: e,
                  },
                  sortBy
                );
              }}
              totalPages={paginationObj.totalPages}
            />
          </div>
        )}
    </>
  );
};

export default ParticipantListTable;
